import { useState } from 'react';
import type { AppSnapshot } from '../../app/state';
import { callAction } from '../../app/actions';
import type { MyTodo } from '../../api/todos';
import { canEditTodos, canViewTodos, isPermHidden } from '../../lib/permissions';
import type { HiddenSets } from '../../lib/permissions';
import { formatDue, hasNotes, isOverdue } from '../popup/TodoSection';
import { todoText } from '../../i18n/todos';
import { SectionHeading } from './SectionHeading';

/**
 * "MY TO-DOS" (V0.50) — the sidebar's first section, above My assignments.
 *
 * GROUPED BY NODE, because that is the unit the work belongs to and a flat list of eleven
 * titles from six different parts of the map is a list nobody can act on. Each group is its own
 * foldable heading carrying the node's colour dot, its label, and how many are open.
 *
 * TWO CLICK TARGETS ON ONE HEADING, deliberately: the chevron folds the group, the LABEL
 * reveals that node on the map — the same `revealNodeOnMap` the per-row locate eye performs
 * elsewhere in this pane. Making the whole heading do one of those would have cost the other,
 * and "take me to it" is the more useful of the two to reach in one click.
 *
 * ROWS ARE NOT FILTERED HERE, unlike every other section in this pane. The house rule is that
 * the server ships bare references and this file resolves visibility at render time — but a
 * to-do is not a node or a document, its title exists nowhere else, and the SERVER has already
 * applied the node-visibility rule before sending it (`/api/me/todos` scopes by
 * `getAllowedNodeIds`). The `isPermHidden` pass below is therefore a belt-and-braces UX layer
 * for a stale payload, exactly as `permHidden` is elsewhere, and never the boundary.
 *
 * TICKING DOES NOT CLOSE THE GROUP. The list refreshes through `todosVersion` rather than by
 * remounting, so the fold state — which lives in the app's day-scoped record, not in a
 * component — is untouched by a row changing under it.
 *
 * CLICKING A ROW OPENS THE TO-DO WHERE IT LIVES (V0.50.1): the node's popup, with that to-do
 * expanded and its description showing. `revealTodo` does the three things this pane cannot —
 * reveal the branch, un-fold the popup's section, and say which row to open — because a to-do
 * has no surface of its own and this pane is a list of pointers into the map.
 *
 * The CHECKBOX is not that target and stops the event: ticking something off is not a request
 * to be taken somewhere, and a list you cannot tick without navigating away is not a list.
 */
export function MyTodos({ s, hidden }: { s: AppSnapshot; hidden: HiddenSets }) {
  const [busy, setBusy] = useState<string | null>(null);

  // No permission, no section — not an empty one. A reader with no to-do feature should not be
  // told they have no to-dos.
  if (!canViewTodos(s.perm)) return null;
  // Before the list has been READ, "empty" is not a fact yet — `myItems.loaded`'s rule.
  if (!s.myTodos.loaded) return null;

  const rows = s.myTodos.todos.filter((t) => s.nodes[t.nodeId] && !isPermHidden(t.nodeId, s.edges, hidden));

  /* Grouped by node, each group keeping the server's order (position within a node). A Map
     preserves insertion order, and the payload arrives sorted by node then position, so the
     groups come out in a stable order without a second sort. */
  const byNode = new Map<string, MyTodo[]>();
  for (const t of rows) {
    const list = byNode.get(t.nodeId);
    if (list) list.push(t);
    else byNode.set(t.nodeId, [t]);
  }

  const tick = async (t: MyTodo) => {
    setBusy(t.id);
    try {
      await callAction('setTodoDone', t.id, t.state !== 'done');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <SectionHeading sectionKey="myitems:todos" label={todoText('myTodos', s.currentLocale)} s={s} />
      {s.collapsedSections.has('myitems:todos') ? null : !byNode.size ? (
        <div className="myitems-empty">{todoText('myEmpty', s.currentLocale)}</div>
      ) : (
        [...byNode].map(([nodeId, list]) => {
          const node = s.nodes[nodeId];
          const groupKey = `myitems:todo:${nodeId}`;
          const folded = s.collapsedSections.has(groupKey);
          const openCount = list.filter((t) => t.state !== 'done').length;
          return (
            <div className="todogroup" key={nodeId} data-todo-node={nodeId}>
              <div className="todogroup-head">
                <button
                  type="button"
                  className="todogroup-fold"
                  aria-expanded={!folded}
                  aria-label={todoText(folded ? 'expand' : 'collapse', s.currentLocale)}
                  onClick={() => callAction('setSectionCollapsed', groupKey, !folded)}
                >
                  <svg className={folded ? 'pp-fold' : 'pp-fold open'} viewBox="0 0 24 24" aria-hidden="true">
                    <use href="#i-chev" />
                  </svg>
                </button>
                {/* The LABEL navigates — see the header's note on the two targets. */}
                <span
                  className="todogroup-name"
                  title={todoText('locate', s.currentLocale)}
                  onClick={() => callAction('revealNodeOnMap', nodeId)}
                >
                  <span className="dot" style={{ background: node?.color || 'var(--accent)' }} />
                  {node?.label || 'Untitled'}
                </span>
                <span className="todogroup-count">{openCount}</span>
              </div>
              {folded
                ? null
                : list.map((t) => {
                    const done = t.state === 'done';
                    const overdue = isOverdue(t.dueDate, t.state);
                    const via = t.via ? (s.groupDirectory.find((g) => g.id === t.via)?.name ?? null) : null;
                    return (
                      <div
                        className={`todorow open-todo${done ? ' done' : ''}`}
                        key={t.id}
                        data-mytodo={t.id}
                        role="button"
                        tabIndex={0}
                        title={
                          via ? `${t.title} · ${todoText('via', s.currentLocale)} ${via}` : t.title
                        }
                        onClick={() => callAction('revealTodo', t.nodeId, t.id)}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') return;
                          e.preventDefault();
                          callAction('revealTodo', t.nodeId, t.id);
                        }}
                      >
                        {/* Always enabled: every row here is one this user is the assignee of,
                            which is exactly the population the server lets tick. `stopPropagation`
                            because the row now navigates — ticking a box must not also take
                            somebody to the other side of the map. */}
                        <input
                          type="checkbox"
                          className="todo-check"
                          checked={done}
                          disabled={busy === t.id}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => void tick(t)}
                        />
                        <span className="todo-title">
                          {/* The same caret the popup's row draws, and for the same reason: it
                              says there is a description to be read, and clicking gets you to
                              it — over there, where there is room for it. */}
                          {hasNotes(t) ? (
                            <svg className="todo-caret" viewBox="0 0 24 24" aria-hidden="true">
                              <use href="#i-chev" />
                            </svg>
                          ) : null}
                          {/* WHICH JOB THIS IS A STEP OF (V0.51.1). A sub-task's title is written
                              to be read under its parent — "Collect the comments" says nothing on
                              its own — and the parent is usually somebody else's row, so it is
                              not in this list to be looked up. The server ships it. */}
                          {t.parentTitle ? (
                            <span className="todo-parent" title={t.parentTitle}>
                              {t.parentTitle}
                              <span aria-hidden="true"> › </span>
                            </span>
                          ) : null}
                          {t.title}
                        </span>
                        {t.dueDate ? (
                          <span className={`todo-due${overdue ? ' overdue' : ''}`}>{formatDue(t.dueDate)}</span>
                        ) : null}
                      </div>
                    );
                  })}
            </div>
          );
        })
      )}
    </>
  );
}

/** Whether the pane should draw the section at all — used by `MyItems` for its own layout. */
export const hasTodoSection = (s: AppSnapshot): boolean => canViewTodos(s.perm) && s.myTodos.loaded;
/** Re-exported so `MyItems` can gate the whole feature on one import. */
export { canEditTodos };
